import type { PrivateLessonInitialScheduleOnSellDTO } from "@/modules/private-lessons/domain/contracts/bulk-schedule";
import type { OffSiteDistance } from "@/modules/private-lessons/domain/lib/off-site-distance";
import {
  addMinutesToTimeHhMm,
  toTimeHHmmss,
} from "@/shared/lib/date-time";
import {
  PRIVATE_LESSON_SLOT_DURATION_SET,
  resolveBulkSessionSlotDurationMinutes,
} from "@/shared/lib/private-lesson-slot-duration";
import { format, parse } from "date-fns";
import type { BulkScheduleFormValues } from "../ui/BulkScheduleForm";

export function mapPrivateLessonFormSessionsToInitialSchedule(
  instructorIds: string[],
  sessions: BulkScheduleFormValues["sessions"],
): PrivateLessonInitialScheduleOnSellDTO {
  const sessionsWithDuration = sessions.map((session) => {
    const durationMinutes = resolveBulkSessionSlotDurationMinutes(session);
    if (durationMinutes <= 0) {
      throw new Error("Invalid session duration");
    }
    if (!PRIVATE_LESSON_SLOT_DURATION_SET.has(durationMinutes)) {
      throw new Error(
        `Session duration must be 15–180 minutes in 15-minute steps (got ${durationMinutes})`,
      );
    }

    const startTime = toTimeHHmmss(session.startTime);
    const endTime = toTimeHHmmss(
      addMinutesToTimeHhMm(session.startTime, durationMinutes),
    );

    let formattedDate: string;
    try {
      const parsedDate = parse(session.date, "dd/MM/yyyy", new Date());
      if (Number.isNaN(parsedDate.getTime())) {
        formattedDate = session.date;
      } else {
        formattedDate = format(parsedDate, "yyyy-MM-dd");
      }
    } catch {
      formattedDate = session.date;
    }

    const loc = session.locationKind ?? "studio";
    const base = {
      date: formattedDate,
      startTime,
      endTime,
      durationMinutes,
      locationKind: loc,
      engagementKind: session.engagementKind ?? "standard",
      offSiteLabel:
        loc === "off_site" ? session.offSiteLabel?.trim() || null : null,
      offSiteDistance:
        loc === "off_site"
          ? (session.offSiteDistance as OffSiteDistance)
          : null,
      roomId: loc === "studio" ? session.roomId?.trim() : undefined,
      notes: session.notes || undefined,
    };
    if (session.instructorIds && session.instructorIds.length > 0) {
      return { ...base, instructorIds: session.instructorIds };
    }
    return base;
  });

  return {
    instructorIds,
    sessions: sessionsWithDuration,
  };
}
